import React, { useState } from 'react';
import { Button, Checkbox, Divider, FormControlLabel, Stack, Typography } from '@mui/material';

import { Modal } from 'components/Modal';

import { useExclusions } from '../api/getExclusions';
import { useExclusionSuppliers } from '../api/getExclusionSuppliers';
import { useUpdateExclusion } from '../api/updateExclusion';
import { useUpdateExcludePerSupplier } from '../api/updateExcludePerSupplier';
import { ExclusionMatrices } from './ExclusionMatrices';
import { hasExclusion } from './ExclusionMatrix';
import { ExclusionSupplierListItem } from './ExclusionSupplierListItem';
import { SetupOverlay } from './SetupOverlay';
import { SupplierExclusionsView } from './SupplierExclusionsView';

export const ExcludedRolesView = ({ resource, id, lot, procurementName, onLotChanged }) => {
  const isAgreement = resource === 'framework_agreements';
  const perSupplier = isAgreement && lot.exclude_per_supplier;
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const [supplier, setSupplier] = useState(null);

  const { data: exclusions, refetch } = useExclusions(resource, id, lot.lot_id);
  const {
    data: suppliers,
    refetch: refetchSuppliers,
    isFetchedAfterMount: areSuppliersFetched,
  } = useExclusionSuppliers(id, lot.lot_id, { enabled: perSupplier });
  const { mutateAsync: updateExclusion } = useUpdateExclusion(resource, id, lot.lot_id);
  const { mutateAsync: updateExcludePerSupplier } = useUpdateExcludePerSupplier(id, lot.lot_id);

  const supplierExclusionCount = suppliers.reduce(
    (count, { excluded_count }) => count + excluded_count - exclusions.length,
    0
  );

  const toggle = (roleId, levelId, excluded) =>
    updateExclusion({ role_id: roleId, level_id: levelId, excluded }).then(() =>
      Promise.all([refetch(), perSupplier && refetchSuppliers()])
    );

  const switchPerSupplier = (enabled) => {
    setIsSwitching(true);
    updateExcludePerSupplier({ enabled })
      .then(() => onLotChanged())
      .catch(() => {})
      .finally(() => {
        setIsSwitching(false);
        setIsConfirmOpen(false);
      });
  };

  const closeSupplier = () => {
    setSupplier(null);
    refetchSuppliers();
  };

  return (
    <Stack gap={3}>
      <ExclusionMatrices
        resource={resource}
        id={id}
        lotId={lot.lot_id}
        isExcluded={(roleId, levelId) => hasExclusion(exclusions, roleId, levelId)}
        onToggle={toggle}
      />

      {isAgreement && (
        <>
          <Divider />
          <FormControlLabel
            sx={{ width: 'fit-content' }}
            control={
              <Checkbox
                checked={lot.exclude_per_supplier}
                disabled={isSwitching}
                onChange={({ target: { checked } }) =>
                  checked || supplierExclusionCount === 0
                    ? switchPerSupplier(checked)
                    : setIsConfirmOpen(true)
                }
              />
            }
            label={<Typography fontWeight={700}>Exclude per supplier</Typography>}
          />
        </>
      )}

      {perSupplier && (
        <Stack gap={1}>
          {areSuppliersFetched && suppliers.length === 0 && (
            <Typography color="text.secondary">This lot has no suppliers yet.</Typography>
          )}
          {suppliers.map((supplier) => (
            <ExclusionSupplierListItem
              key={supplier.fac_id}
              onOpen={() => setSupplier(supplier)}
              {...supplier}
            />
          ))}
        </Stack>
      )}

      {supplier && (
        <SetupOverlay
          procurementName={procurementName}
          crumbs={[
            { label: 'Excluded roles', onClick: closeSupplier },
            { label: lot.name, onClick: closeSupplier },
            { label: supplier.name },
          ]}
          onClose={closeSupplier}
        >
          <SupplierExclusionsView id={id} lot={lot} supplier={supplier} />
        </SetupOverlay>
      )}

      <Modal
        title="Exclude per supplier"
        open={isConfirmOpen}
        handleClose={() => setIsConfirmOpen(false)}
      >
        <Typography variant="body1" fontWeight={500}>
          {supplierExclusionCount === 1
            ? '1 exclusion set on suppliers will be removed.'
            : `${supplierExclusionCount} exclusions set on suppliers will be removed.`}{' '}
          Exclusions for all suppliers stay as they are.
        </Typography>
        <Stack direction="row" justifyContent="center" gap={1}>
          <Button variant="outlined" onClick={() => setIsConfirmOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={isSwitching}
            onClick={() => switchPerSupplier(false)}
          >
            Confirm
          </Button>
        </Stack>
      </Modal>
    </Stack>
  );
};
